import React from "react";
import { Eye, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface HoverAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: "default" | "danger";
}

export interface HoverActionsMenuProps {
  actions?: HoverAction[];
  onOpen?: () => void;
  onDelete?: () => void;
  className?: string;
}

const HoverActionsMenu: React.FC<HoverActionsMenuProps> = ({
  actions,
  onOpen,
  onDelete,
  className = "",
}) => {
  const { t } = useTranslation();

  // Build actions from props or use default actions
  const menuActions: HoverAction[] = React.useMemo(() => {
    if (actions) return actions;

    const defaultActions: HoverAction[] = [];
    if (onOpen) {
      defaultActions.push({
        icon: <Eye className="w-3 h-3 text-gray-500" />,
        label: t("Open") as string,
        onClick: onOpen,
        variant: "default",
      });
    }
    if (onDelete) {
      defaultActions.push({
        icon: <Trash2 className="w-3 h-3 text-red-500" />,
        label: t("Delete") as string,
        onClick: onDelete,
        variant: "danger",
      });
    }
    return defaultActions;
  }, [actions, onOpen, onDelete, t]);

  if (menuActions.length === 0) return null;

  return (
    <div
      className={`absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white rounded-md shadow-sm border border-gray-200 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10 ${className}`}
    >
      {menuActions.map((action, index) => (
        <button
          key={index}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
          className={`p-1 rounded transition-colors flex items-center justify-center ${
            action.variant === "danger" ? "hover:bg-red-50" : "hover:bg-gray-100"
          }`}
          title={action.label}
        >
          {action.icon}
        </button>
      ))}
    </div>
  );
};

export default HoverActionsMenu;
